import * as http from './http';
import ApiService from './service';
import { ApiResponse, ListSort, PaginatedApiResponse } from '../models';
import { Dict } from '../../models';
import {
  LeadConfiguration,
  TradeInAssist,
  TradeInEvaluation,
  TradeInInfo,
} from '../models/configuration';
import { stringify } from 'query-string';

export class TradeInService extends ApiService {
  public async getTradeIns(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<TradeInInfo> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/trade-in/list`;
    const response = await http.post<PaginatedApiResponse<TradeInInfo>>(
      url,
      data,
      this.store
    );
    return { ...response?.data, lastUpdatedAt: new Date() };
  }

  public async getTradeInDetails(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
  }): Promise<{
    tradeIn: TradeInInfo;
    configuration: LeadConfiguration;
  }> {
    const url = `${this.apiDomain}/api/trade-in?${stringify(data)}`;
    const response = await http.get<
      ApiResponse<{
        tradeIn: TradeInInfo;
        configuration: LeadConfiguration;
      }>
    >(url, this.store);
    return response?.data?.data;
  }

  public async attachTradeIn(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/attach`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async detachTradeIn(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/detach`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async addEvaluationToTradeIn(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    evaluation: TradeInEvaluation;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/evaluation`;
    const response = await http.post<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async updateTradeInEvaluation(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    evaluation: TradeInEvaluation;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/evaluation`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async withdrawTradeInEvaluation(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/evaluation/withdraw`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async rejectByEvaluator(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    comment?: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/reject-by-evaluator`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async holdTradeIn(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    comment?: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/hold`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async sendTradeInReminder(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    comment?: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/reminder`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async requestTradeInNegotiation(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    comment?: string;
    amount: number;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/negotiation`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async acceptByCustomer(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    comment?: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/approve`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async rejectByCustomer(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
    comment?: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/reject-by-customer`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async applyTradeInAssist(
    data: {
      leadId: string;
      configId: string;
      tradeInId: string;
    } & TradeInAssist
  ): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/assist`;
    const response = await http.post<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }

  public async removeTradeInAssist(data: {
    leadId: string;
    configId: string;
    tradeInId: string;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/trade-in/assist/remove`;
    const response = await http.put<ApiResponse<void>>(url, data, this.store);
    return response?.data?.data;
  }
}
